import React, { useMemo, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { mdiRobot } from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { useUser } from "../../providers/UserProvider";
import { User } from "../../storage/store";
import { DEVICE_TYPES } from "../../constants/devices";
import useAlive from "../../hooks/useAlive";
import usePropertyDevices from "../../hooks/usePropertyDevices";
import { AutomationService } from "../../automation/service";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import { actionKind } from "../../automation/actions";
import {
  canEditAutomation,
  confirmMessage,
  errorMessage,
  useCanManageAutomations,
  useConfirm,
} from "../../automation/useAutomations";
import HomeLocationSheet from "./HomeLocationSheet";
import { StepTitle } from "./create/controls";
import { DetailsStep, TriggerStep } from "./create/StartSteps";
import ConditionsStep from "./create/ConditionsStep";
import ActionsStep from "./create/ActionsStep";
import SaveStep from "./create/SaveStep";
import { CONDITION_TYPES, draftOf, missing, newDraft, requestOf, sensorEvents, suggestedName } from "./create/draft";
import { AUTOMATION_PATHS } from "./paths";
import "../../assets/styles/app-automation.css";

const WITH_DETAILS = ["schedule", "sun", "timer", "rule", "location", "inching", "cycling"];
// what step 1 called the choice
const DETAIL_TITLES = {
  schedule: "Time",
  sun: "Sunrise/Sunset",
  timer: "Countdown",
  rule: "A device event",
  location: "Arriving or leaving home",
  inching: "Inching",
  cycling: "Cycling",
};
const ONE_DEVICE = ["inching", "cycling"];

// The steps a type goes through: what starts it (and its details), only if,
// then do this, name and save. A schedule's only "only if" is another device,
// so it skips the step in a home without door sensors.
function stepsFor(type, typeFixed, hasDoors) {
  const steps = [];
  if (!typeFixed) steps.push("trigger");
  if (WITH_DETAILS.includes(type)) steps.push("details");
  if (CONDITION_TYPES.includes(type) && (type !== "schedule" || hasDoors)) steps.push("conditions");
  if (!ONE_DEVICE.includes(type)) steps.push("actions");
  steps.push("save");
  return steps;
}

// Which of the step numbers shown ("Step 2 of 4") a step is: picking the type
// and its details are both step 1
const shownNumber = (steps, step) => steps.filter((s) => s !== "trigger").indexOf(step) + 1 || 1;
const shownCount = (type, steps) => (type ? steps.filter((s) => s !== "trigger").length || 1 : 3);

// The step a field the API turned down belongs to
const FIELD_STEP = [
  ["trigger", "details"],
  ["location", "details"],
  ["conditions", "conditions"],
  ["actions", "actions"],
  ["name", "save"],
];

// The flow itself (app/screens/Automation/AutomationCreate.js). editing is the
// automation to change; params are { type, applianceId, event } to start from
// a device. The whole draft stays in this page until it's saved.
function CreateFlow({ editing, params }) {
  const navigate = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const { profileName } = useUser();
  const { homeId, capabilities, ready, device: capsDevice, homeDevice, lookup } = useAutomationFeature();
  const { confirm, dialog } = useConfirm();

  const [draft, setDraft] = useState(() => {
    if (editing) return draftOf(editing, homeDevice);
    const { type, applianceId, event } = params || {};
    const start = newDraft(type, ONE_DEVICE.includes(type) ? applianceId : undefined);
    if (type === "rule" && applianceId && capsDevice(applianceId)?.ruleTrigger) {
      // a sensor's Add sheet: the sensor starts the rule
      const events = sensorEvents(capsDevice(applianceId));
      const found = events.find((e) => e.key === event) || events[0];
      start.trigger = { applianceId, fact: found?.fact || null, value: found ? found.value : null };
    } else if (applianceId && type && !ONE_DEVICE.includes(type)) {
      // any other device's Add sheet: the device is one of the things it does
      const kind = actionKind(homeDevice(applianceId));
      if (kind) start.actions = [{ applianceId, choice: kind.choices[0].key, level: kind.level?.initial }];
    }
    return start;
  });
  const typeFixed = Boolean(editing) || Boolean(params?.type);
  const hasDoors = (capabilities?.devices || []).some(
    (d) => d.deviceType === DEVICE_TYPES.DW_SENSOR && d.ruleTrigger && !d.disabled
  );
  const steps = stepsFor(draft.type, typeFixed, hasDoors);
  const [stepIndex, setStepIndex] = useState(0);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const [saving, setSaving] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  // who is signed in, to say when an automation's notifications go to someone else
  const me = User.getUser();
  const homeName = profileName.find((house) => house.id === homeId)?.name;

  const devices = useMemo(() => capabilities?.devices || [], [capabilities]);
  const sensors = useMemo(() => devices.filter((d) => d.ruleTrigger && !d.disabled), [devices]);
  const doors = useMemo(() => sensors.filter((d) => d.deviceType === DEVICE_TYPES.DW_SENSOR), [sensors]);
  // devices it can control: in this home, shared with the person, able to take part, and settable from here
  const eligible = useMemo(
    () =>
      devices.filter(
        (d) =>
          !d.disabled &&
          (d.types || []).includes(draft.type) &&
          (draft.type !== "location" || d.location) &&
          actionKind(homeDevice(d.applianceId))
      ),
    [devices, draft.type, homeDevice]
  );

  const update = (changes) => setDraft((current) => ({ ...current, ...changes }));
  // a new step opens at the top: the page scrolls, not a box in it
  const goTo = (index) => {
    setStepIndex(index);
    window.scrollTo(0, 0);
  };
  // Cancel, and where saving ends up: back where it was opened from
  const leave = () => {
    if (navigate.length > 1) navigate.goBack();
    else navigate.push(AUTOMATION_PATHS.HOME);
  };

  // Devices picked before the type changed stay only where the new type can use
  // them; a "toggle" is for tap-to-run alone. (The app keeps them all, and the
  // API then turns the save down.)
  const pickType = (type) => {
    setDraft((current) => ({
      ...newDraft(type),
      actions: current.actions.filter(
        (action) =>
          (action.choice !== "toggle" || ["tap", "nfc"].includes(type)) &&
          (capsDevice(action.applianceId)?.types || []).includes(type)
      ),
    }));
    const following = stepsFor(type, false, hasDoors);
    goTo(following.length > 1 ? 1 : 0);
    if (["sun", "location"].includes(type) && !capabilities?.home?.location) setLocationOpen(true);
  };

  const back = () => {
    if (stepIndex === 0) leave();
    else goTo(stepIndex - 1);
  };

  const save = async (confirmed) => {
    setSaving(true);
    try {
      const request = { ...requestOf(draft, homeDevice), ...(confirmed ? { confirmed: true } : {}) };
      const saved = editing
        ? await AutomationService.update(homeId, editing.id, request)
        : await AutomationService.create(homeId, request);
      toast(editing ? "Automation saved." : `${saved.name} saved.`, "green");
      leave();
    } catch (error) {
      if (error?.status === 409 && error.confirm) {
        setSaving(false);
        if (await confirm({ title: "Save it?", message: confirmMessage(error), confirmLabel: "Save" })) await save(true);
        return;
      }
      const field = error?.field || "";
      const target = FIELD_STEP.find(([prefix]) => field.startsWith(prefix));
      if (target && steps.includes(target[1])) goTo(steps.indexOf(target[1]));
      if (field === "location") setLocationOpen(true);
      toast(errorMessage(error), "red");
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const next = () => {
    const problem = missing(step, draft, capabilities?.home);
    if (problem) {
      if (step === "details" && ["sun", "location"].includes(draft.type) && !capabilities?.home?.location) {
        setLocationOpen(true);
      }
      toast(problem, "red");
      return;
    }
    if (step === "save") {
      save(false);
      return;
    }
    const following = steps[stepIndex + 1];
    // A suggested name follows the trigger (going back and picking sunrise renames "at sunset"); a typed one stays
    if (following === "save" && (!draft.name.trim() || !draft.nameTyped)) update({ name: suggestedName(draft, lookup) });
    goTo(stepIndex + 1);
  };

  const last = step === "save";
  const preview = useMemo(() => {
    if (step !== "save") return null;
    const request = requestOf(draft, homeDevice);
    const trigger =
      draft.type === "timer" ? { runAt: moment().add(draft.trigger.delayMinutes, "minutes").toISOString() } : request.trigger;
    return { ...request, type: draft.type, trigger };
  }, [step, draft, homeDevice]);

  const titles = {
    trigger: { name: "What starts it", title: "What starts it?" },
    details: { name: DETAIL_TITLES[draft.type], title: DETAIL_TITLES[draft.type] },
    conditions: { name: "Only if (optional)", title: "Only if…", hint: "Optional. Leave these off to run it every time." },
    actions: {
      name: "Then do this",
      title: "Then do this",
      hint: "Only devices you can use that support this are listed.",
    },
    save: { name: "Name and save", title: null },
  };
  const count = shownCount(draft.type, steps);
  const number = step === "trigger" ? 1 : shownNumber(steps, step);

  return (
    <Main
      header="stack"
      title={editing ? "Edit automation" : "New automation"}
      onBack={leave}
      hideTabBar
      headerRight={
        step === "trigger" ? null : (
          <button type="button" className="hx-auto-next" onClick={next} disabled={saving}>
            {last ? "Save" : "Next"}
          </button>
        )
      }
    >
      <div className="hx-auto-create">
        <div className="hx-auto-progress" aria-hidden="true">
          {Array.from({ length: count }).map((_, index) => (
            <span key={index} className={index < number ? "is-done" : ""} />
          ))}
        </div>
        <StepTitle step={number} of={count} {...titles[step]} />
        {step === "trigger" ? <TriggerStep ready={ready} sensors={sensors} onPick={pickType} /> : null}
        {step === "details" ? (
          <DetailsStep
            draft={draft}
            onTrigger={(trigger) => update({ trigger })}
            home={capabilities?.home}
            onSetLocation={() => setLocationOpen(true)}
            sensors={sensors}
            lookup={lookup}
          />
        ) : null}
        {step === "conditions" ? (
          <ConditionsStep
            type={draft.type}
            draft={draft}
            onConditions={(conditions) => update({ conditions })}
            doors={doors}
            lookup={lookup}
          />
        ) : null}
        {step === "actions" ? (
          <ActionsStep
            draft={draft}
            onActions={(actions) => update({ actions })}
            eligible={eligible}
            homeDevice={homeDevice}
            capsDevice={capsDevice}
            allowToggle={draft.type === "tap" || draft.type === "nfc"}
          />
        ) : null}
        {step === "save" && preview ? (
          <SaveStep
            draft={draft}
            preview={preview}
            onName={(name) => update({ name, nameTyped: Boolean(name.trim()) })}
            onNotify={(notify) => update({ notify, notifyChanged: true })}
            lookup={lookup}
            me={me}
          />
        ) : null}
        {step !== "trigger" ? (
          <div className="hx-auto-step-buttons">
            {stepIndex > 0 ? (
              <button type="button" className="hx-btn is-ghost" onClick={back}>
                Back
              </button>
            ) : null}
            <button type="button" className="hx-btn" onClick={next} disabled={saving}>
              {saving ? "Saving…" : last ? "Save" : "Next"}
            </button>
          </div>
        ) : null}
      </div>
      <HomeLocationSheet
        open={locationOpen}
        homeName={homeName}
        onClose={() => setLocationOpen(false)}
        onSaved={() => setLocationOpen(false)}
      />
      {dialog}
    </Main>
  );
}

// + New, a device's Add sheet, or Edit on an automation's page. Owners only,
// as in the app. A page opened straight from its address waits for the home's
// capabilities and devices, which the draft starts from.
function AutomationCreate() {
  const { pathname, state } = useLocation();
  const { checked, on } = useAutomationFeature();
  const { allDevices, profileName } = useUser();
  const canManage = useCanManageAutomations();
  // the home's devices with their pins, traits and models, which actions are built from
  const { devices } = usePropertyDevices();
  const editing = pathname === AUTOMATION_PATHS.EDIT ? state?.automation : null;
  const title = editing ? "Edit automation" : "New automation";

  if (pathname === AUTOMATION_PATHS.EDIT && !editing) return <Redirect to={AUTOMATION_PATHS.HOME} />;

  // the homes and roles come with the page's header; until then nobody is an owner yet
  if (!checked || !profileName?.length || (devices === null && !allDevices?.length)) {
    return (
      <Main header="stack" title={title} hideTabBar>
        <div className="hx-auto-create">
          <div className="hx-skel hx-auto-skel is-tall" />
        </div>
      </Main>
    );
  }
  let problem = null;
  if (!on) problem = "Automations aren't on for this home.";
  else if (!canManage) problem = "Only owners can add or change automations.";
  else if (editing && !canEditAutomation(editing)) problem = "This automation can't be changed here.";
  if (problem) {
    return (
      <Main header="stack" title={title} hideTabBar>
        <div className="hx-empty-page hx-auto-empty" role="alert">
          <MdiIcon path={mdiRobot} size={64} />
          <p>{problem}</p>
        </div>
      </Main>
    );
  }
  return <CreateFlow editing={editing} params={editing ? null : state} />;
}

export default AutomationCreate;
